"use client"

import { Trans } from "@lingui/react/macro"
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import type { EmailConsentChoices } from "./email-consent-choices"

type LinkedEmailPromptDialogProps = {
  open: boolean
  onDismiss: () => void
  onAddEmail: () => void
  mode?: "bind-email" | "turn-on-alerts" | "settings"
  pending?: boolean
  error?: string | null
  actionLabel?: string
}

export const LinkedEmailPromptDialog = reatomComponent(
  ({
    open,
    onDismiss,
    onAddEmail,
    mode = "bind-email",
    pending = false,
    error,
    actionLabel,
  }: LinkedEmailPromptDialogProps) => {
    return (
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !pending) onDismiss()
        }}
      >
        <DialogContent
          className={
            mode === "settings"
              ? "max-w-md p-6"
              : "max-h-[90dvh] max-w-md overflow-y-auto p-6"
          }
          overlayClassName="linked-email-prompt-overlay"
        >
          {mode !== "settings" && (
            <div className="-mx-6 -mt-6 overflow-hidden rounded-t-lg">
              <img
                src="/images/promo/email-updates-banner.jpg"
                alt=""
                className="block h-auto w-full"
              />
            </div>
          )}
          <DialogHeader className="space-y-3 text-left">
            <DialogTitle className="font-headline text-2xl">
              {mode === "settings" ? (
                <Trans>Add Email Login</Trans>
              ) : (
                <Trans>Stay Tuned for Updates</Trans>
              )}
            </DialogTitle>
            <DialogDescription className="leading-relaxed">
              {mode === "settings" ? (
                <Trans>
                  By adding my email, I agree to receive emails from Rarible in
                  accordance with the{" "}
                  <a
                    href="https://rarible.com/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="underline underline-offset-2"
                  >
                    Privacy Policy
                  </a>
                  . Trading Activity email will be enabled after verification.
                  Other notification choices stay as you set them.
                </Trans>
              ) : mode === "turn-on-alerts" ? (
                <Trans>
                  Get notified about price changes, offers, drops, and
                  collection activity. Manage preferences anytime in Settings.
                </Trans>
              ) : (
                <Trans>
                  Add your email to get alerts for price changes, offers, drops,
                  and collection activity. Manage your email preferences anytime
                  in Settings.
                </Trans>
              )}
            </DialogDescription>
          </DialogHeader>
          {mode !== "settings" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {mode === "turn-on-alerts" ? (
                  <Trans>
                    By turning on alerts, you agree to receive Rarible emails.
                    Read our{" "}
                  </Trans>
                ) : (
                  <Trans>
                    By adding my email, I agree to receive emails from Rarible
                    in accordance with the{" "}
                  </Trans>
                )}{" "}
                <a
                  className="underline"
                  href="https://rarible.com/privacy"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Trans>Privacy Policy</Trans>
                </a>
                .
              </p>
            </div>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter
            className={
              mode === "settings"
                ? "mt-6 flex-row justify-end gap-3 sm:space-x-0"
                : "gap-2 sm:flex-row sm:space-x-0"
            }
          >
            <Button
              className={mode === "settings" ? undefined : "w-full"}
              type="button"
              variant="outline"
              onClick={onDismiss}
              disabled={pending}
            >
              {mode === "settings" ? (
                <Trans>Cancel</Trans>
              ) : (
                <Trans>Maybe Later</Trans>
              )}
            </Button>
            <Button
              className={mode === "settings" ? undefined : "w-full"}
              type="button"
              onClick={onAddEmail}
              disabled={pending}
            >
              {pending ? (
                <Trans>Saving…</Trans>
              ) : (
                (actionLabel ??
                (mode === "turn-on-alerts" ? (
                  <Trans>Turn On Alerts</Trans>
                ) : mode === "settings" ? (
                  <Trans>Add Email</Trans>
                ) : (
                  <Trans>Add My Email</Trans>
                )))
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  },
  "LinkedEmailPromptDialog",
)

type LinkedEmailSuccessDialogProps = {
  open: boolean
  choices: EmailConsentChoices
  onDone: () => void
}

export const LinkedEmailSuccessDialog = reatomComponent(
  ({ open, choices, onDone }: LinkedEmailSuccessDialogProps) => (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onDone()
      }}
    >
      <DialogContent className="max-w-md p-6">
        <DialogHeader className="space-y-3 text-left">
          <DialogTitle className="font-headline text-2xl">
            <Trans>You’re all set</Trans>
          </DialogTitle>
          <DialogDescription className="space-y-2 leading-relaxed">
            {choices.notificationConsent && (
              <span className="block">
                <Trans>
                  We’ll keep you updated on your watchlist, collection, and
                  offers.
                </Trans>
              </span>
            )}
            {choices.marketingConsent && (
              <span className="block">
                <Trans>
                  You’ll receive product news and updates from Rarible.
                </Trans>
              </span>
            )}
            {!choices.notificationConsent && !choices.marketingConsent && (
              <span className="block">
                <Trans>
                  Your email is linked. Choose the updates you want in Settings.
                </Trans>
              </span>
            )}
            <span className="block">
              <Trans>Customize your updates anytime in Settings.</Trans>
            </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:flex-row sm:space-x-0">
          <form action="/settings" className="w-full">
            <Button className="w-full" type="submit" variant="outline">
              <Trans>Manage notifications</Trans>
            </Button>
          </form>
          <Button className="w-full" type="button" onClick={onDone}>
            <Trans>Done</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ),
  "LinkedEmailSuccessDialog",
)

type EmailConsentRecoveryDialogProps = {
  open: boolean
  isSaving: boolean
  onClose: () => void
  onRetry: () => void
}

export const EmailConsentRecoveryDialog = reatomComponent(
  ({ open, isSaving, onClose, onRetry }: EmailConsentRecoveryDialogProps) => (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose()
      }}
    >
      <DialogContent className="max-w-md p-6">
        <DialogHeader className="space-y-3 text-left">
          <DialogTitle className="font-headline text-2xl">
            <Trans>Email preferences need saving</Trans>
          </DialogTitle>
          <DialogDescription className="leading-relaxed">
            <Trans>
              Email added. We couldn’t save your email preferences. Try again to
              finish setting them up.
            </Trans>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:flex-row sm:space-x-0">
          <Button
            className="w-full"
            type="button"
            variant="outline"
            onClick={onClose}
          >
            <Trans>Dismiss</Trans>
          </Button>
          <Button
            className="w-full"
            type="button"
            disabled={isSaving}
            onClick={onRetry}
          >
            <Trans>Try again</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ),
  "EmailConsentRecoveryDialog",
)
